import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../../services/apiClient';
import { toastService } from '../../../services/toastService';
import './PersonalContextModal.css';

// Supported external providers for conversation import
// Logos: official brand assets from Wikimedia Commons
const SUPPORTED_PROVIDERS = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/ChatGPT-Logo.svg',
    description: 'Import conversations from OpenAI ChatGPT',
    acceptedFormats: '.json',
    helpText: 'Export your ChatGPT conversations from Settings → Data controls → Export data'
  },
  {
    id: 'claude',
    name: 'Claude',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b0/Claude_AI_symbol.svg',
    description: 'Import conversations from Anthropic Claude',
    acceptedFormats: '.json',
    helpText: 'Export your Claude conversations from your account settings'
  }
];

export default function PersonalContextModal({ isOpen, onClose }) {
  const [importedContexts, setImportedContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [stats, setStats] = useState({
    totalConversations: 0,
    totalMessages: 0,
    lastImport: null
  });
  const [expandedContext, setExpandedContext] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  
  // Import preview state
  const [previewData, setPreviewData] = useState(null);
  const [selectedConversationsToImport, setSelectedConversationsToImport] = useState(new Set());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // Conversation browser state
  const [showConversationBrowser, setShowConversationBrowser] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedImportFilter, setSelectedImportFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [conversationsPagination, setConversationsPagination] = useState({
    total: 0,
    skip: 0,
    limit: 20,
    hasMore: false
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPersonalContexts();
    }
  }, [isOpen]);

  const loadPersonalContexts = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getPersonalContexts();
      if (response && response.contexts) {
        setImportedContexts(response.contexts);
        setStats({
          totalConversations: response.stats?.total_conversations || 0,
          totalMessages: response.stats?.total_messages || 0,
          lastImport: response.stats?.last_import || null
        });
      }
    } catch (error) {
      console.error('Error loading personal contexts:', error);
      // Don't show error toast on initial load if no data exists
      if (error.response?.status !== 404) {
        toastService.error('Failed to load personal contexts');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load conversations for the browser
  const loadConversations = async (importId = '', search = '', skip = 0) => {
    setConversationsLoading(true);
    try {
      const response = await apiClient.getPersonalConversations({
        importId: importId || undefined,
        search: search || undefined,
        skip,
        limit: conversationsPagination.limit
      });
      
      if (response) {
        setConversations(response.conversations || []);
        setConversationsPagination({
          total: response.total || 0,
          skip: response.skip || 0,
          limit: response.limit || 20,
          hasMore: response.has_more || false
        });
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      toastService.error('Failed to load conversations');
    } finally {
      setConversationsLoading(false);
    }
  };

  // Load conversation detail
  const loadConversationDetail = async (conversationId) => {
    setLoadingDetail(true);
    try {
      const response = await apiClient.getPersonalConversationDetail(conversationId);
      if (response) {
        setConversationDetail(response);
        setSelectedConversation(conversationId);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      toastService.error('Failed to load conversation');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Open conversation browser
  const openConversationBrowser = () => {
    setShowConversationBrowser(true);
    setSelectedImportFilter('');
    setSearchTerm('');
    loadConversations();
  };

  // Handle filter change
  const handleFilterChange = (importId) => {
    setSelectedImportFilter(importId);
    loadConversations(importId, searchTerm, 0);
  };

  // Handle search
  const handleSearch = () => {
    loadConversations(selectedImportFilter, searchTerm, 0);
  };

  // Handle pagination
  const loadMoreConversations = () => {
    const newSkip = conversationsPagination.skip + conversationsPagination.limit;
    loadConversations(selectedImportFilter, searchTerm, newSkip);
  };

  // Close conversation detail
  const closeConversationDetail = () => {
    setSelectedConversation(null);
    setConversationDetail(null);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setShowImportModal(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setPreviewLoading(true);
    setImportFile(file);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('provider', selectedProvider.id);

      const response = await apiClient.previewPersonalContext(formData);

      if (response && response.conversations) {
        setPreviewData(response);
        // Select all conversations by default
        const allIndices = new Set(response.conversations.map(c => c.index));
        setSelectedConversationsToImport(allIndices);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toastService.error(error.message || 'Failed to preview file');
    } finally {
      setPreviewLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle conversation selection for import
  const toggleConversationSelection = (index) => {
    setSelectedConversationsToImport(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Select/Deselect all conversations
  const toggleSelectAll = () => {
    if (previewData) {
      if (selectedConversationsToImport.size === previewData.conversations.length) {
        setSelectedConversationsToImport(new Set());
      } else {
        setSelectedConversationsToImport(new Set(previewData.conversations.map(c => c.index)));
      }
    }
  };

  // Confirm import with selected conversations
  const handleConfirmImport = async () => {
    if (!importFile || selectedConversationsToImport.size === 0) return;

    setImporting(true);
    setImportProgress({ stage: 'uploading', percent: 0, message: 'Importing selected conversations...' });

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('provider', selectedProvider.id);
      formData.append('selected_indices', Array.from(selectedConversationsToImport).join(','));

      const response = await apiClient.importPersonalContext(formData, (progress) => {
        setImportProgress({
          stage: 'processing',
          percent: progress,
          message: `Processing conversations... ${progress}%`
        });
      });

      if (response && response.success) {
        setImportProgress({ stage: 'complete', percent: 100, message: 'Import completed!' });
        toastService.success(`Successfully imported ${response.conversations_imported || 0} conversations`);
        
        // Reload the contexts
        await loadPersonalContexts();
        
        // Close modal after a short delay
        setTimeout(() => {
          resetImportModal();
        }, 1500);
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportProgress({ stage: 'error', percent: 0, message: error.message || 'Import failed' });
      toastService.error(error.message || 'Failed to import conversations');
    } finally {
      setImporting(false);
    }
  };

  // Reset import modal state
  const resetImportModal = () => {
    setShowImportModal(false);
    setSelectedProvider(null);
    setImportProgress(null);
    setPreviewData(null);
    setSelectedConversationsToImport(new Set());
    setImportFile(null);
  };

  const handleDeleteContext = async (contextId) => {
    try {
      await apiClient.deletePersonalContext(contextId);
      toastService.success('Context deleted successfully');
      setDeleteConfirm(null);
      await loadPersonalContexts();
    } catch (error) {
      console.error('Error deleting context:', error);
      toastService.error('Failed to delete context');
    }
  };

  const handleDeleteAllContexts = async () => {
    try {
      await apiClient.deleteAllPersonalContexts();
      toastService.success('All personal contexts deleted');
      setDeleteConfirm(null);
      await loadPersonalContexts();
    } catch (error) {
      console.error('Error deleting all contexts:', error);
      toastService.error('Failed to delete all contexts');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProviderDisplay = (providerId) => {
    const provider = SUPPORTED_PROVIDERS.find(p => p.id === providerId);
    if (!provider) return { icon: 'fas fa-file' };
    return { logo: provider.logo, icon: provider.icon || 'fas fa-file' };
  };

  const getProviderName = (providerId) => {
    const provider = SUPPORTED_PROVIDERS.find(p => p.id === providerId);
    return provider?.name || providerId;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content personal-context-modal-wrapper" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>
              <i className="fas fa-user-lock"></i>
              Personal Context
            </h2>
            <button 
              className="close-btn" 
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-body">
            <div className="personal-context-container">
              {/* Privacy Notice */}
              <div className="privacy-notice">
                <i className="fas fa-lock"></i>
                <div className="notice-content">
                  <strong>Private Data</strong>
                  <p>Your imported conversations are isolated to your account. No other user in your organization can see or use your personal context.</p>
                </div>
              </div>

              {/* Two-column layout for stats and import */}
              <div className="personal-context-top-section">
                {/* Left Column: Stats Cards */}
                <div className="stats-column">
                  <div className="personal-context-stats">
                    <div className="stat-card">
                      <div className="stat-icon">
                        <i className="fas fa-comments"></i>
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{stats.totalConversations}</span>
                        <span className="stat-label">Conversations</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon">
                        <i className="fas fa-message"></i>
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{stats.totalMessages}</span>
                        <span className="stat-label">Messages</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon">
                        <i className="fas fa-clock"></i>
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{stats.lastImport ? formatDate(stats.lastImport) : 'Never'}</span>
                        <span className="stat-label">Last Import</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Import Section */}
                <div className="import-section">
                  <h3>
                    <i className="fas fa-cloud-upload-alt"></i>
                    Import from External Providers
                  </h3>
                  <p className="section-description">
                    Select a provider to import your conversation history. Supported formats include JSON exports from ChatGPT and Claude.
                  </p>
                  
                  <div className="provider-grid">
                    {SUPPORTED_PROVIDERS.map(provider => (
                      <div 
                        key={provider.id}
                        className="provider-card"
                        onClick={() => handleProviderSelect(provider)}
                      >
                        <div className="provider-icon provider-logo">
                          {provider.logo ? (
                            <img src={provider.logo} alt={provider.name} />
                          ) : (
                            <i className={provider.icon}></i>
                          )}
                        </div>
                        <div className="provider-info">
                          <h4>{provider.name}</h4>
                          <p>{provider.description}</p>
                          <span className="provider-formats">
                            <i className="fas fa-file-code"></i>
                            {provider.acceptedFormats}
                          </span>
                        </div>
                        <div className="provider-action">
                          <i className="fas fa-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Imported Contexts List */}
              <div className="contexts-section">
                <div className="section-header">
                  <h3>
                    <i className="fas fa-history"></i>
                    Imported Conversations
                  </h3>
                  <div className="section-header-actions">
                    {stats.totalConversations > 0 && (
                      <button 
                        className="btn btn-browse"
                        onClick={openConversationBrowser}
                      >
                        <i className="fas fa-folder-open"></i>
                        Browse
                      </button>
                    )}
                    {importedContexts.length > 0 && (
                      <button 
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => setDeleteConfirm('all')}
                      >
                        <i className="fas fa-trash-alt"></i>
                        Delete All
                      </button>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading your personal contexts...</p>
                  </div>
                ) : importedContexts.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <h4>No Imported Conversations</h4>
                    <p>Import your conversation history from ChatGPT, Claude, or other providers to use as personal context.</p>
                  </div>
                ) : (
                  <div className="contexts-list">
                    {importedContexts.map(context => (
                      <div 
                        key={context.id} 
                        className={`context-card ${expandedContext === context.id ? 'expanded' : ''}`}
                      >
                        <div 
                          className="context-header"
                          onClick={() => setExpandedContext(expandedContext === context.id ? null : context.id)}
                        >
                          <div className="context-provider">
                            {getProviderDisplay(context.provider).logo ? (
                              <img src={getProviderDisplay(context.provider).logo} alt={getProviderName(context.provider)} className="context-provider-logo" />
                            ) : (
                              <i className={getProviderDisplay(context.provider).icon}></i>
                            )}
                            <span>{getProviderName(context.provider)}</span>
                          </div>
                          <div className="context-meta">
                            <span className="context-conversations">
                              <i className="fas fa-comments"></i>
                              {context.conversation_count} conversations
                            </span>
                            <span className="context-messages">
                              <i className="fas fa-message"></i>
                              {context.message_count} messages
                            </span>
                            <span className="context-date">
                              <i className="fas fa-calendar"></i>
                              {formatDate(context.imported_at)}
                            </span>
                          </div>
                          <div className="context-actions">
                            <button 
                              className="btn-icon expand-btn"
                              title={expandedContext === context.id ? 'Collapse' : 'Expand'}
                            >
                              <i className={`fas fa-chevron-${expandedContext === context.id ? 'up' : 'down'}`}></i>
                            </button>
                            <button 
                              className="btn-icon delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(context.id);
                              }}
                              title="Delete this import"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                        
                        {expandedContext === context.id && (
                          <div className="context-details">
                            <div className="context-detail-item">
                              <label>Import ID:</label>
                              <code>{context.id}</code>
                            </div>
                            <div className="context-detail-item">
                              <label>File Name:</label>
                              <span>{context.original_filename || 'Unknown'}</span>
                            </div>
                            <div className="context-detail-item">
                              <label>Date Range:</label>
                              <span>
                                {context.date_range?.start 
                                  ? `${formatDate(context.date_range.start)} - ${formatDate(context.date_range.end)}`
                                  : 'Not available'
                                }
                              </span>
                            </div>
                            {context.sample_topics && context.sample_topics.length > 0 && (
                              <div className="context-detail-item">
                                <label>Sample Topics:</label>
                                <div className="topic-tags">
                                  {context.sample_topics.map((topic, idx) => (
                                    <span key={idx} className="topic-tag">{topic}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && selectedProvider && (
        <div className="modal-overlay" onClick={() => !importing && !previewLoading && resetImportModal()}>
          <div className={`import-modal ${previewData ? 'expanded' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {selectedProvider.logo ? (
                  <img src={selectedProvider.logo} alt={selectedProvider.name} className="modal-provider-logo" />
                ) : (
                  <i className={selectedProvider.icon}></i>
                )}
                <h3>
                  {previewData 
                    ? `Select Conversations to Import` 
                    : `Import from ${selectedProvider.name}`}
                </h3>
              </div>
              {!importing && !previewLoading && (
                <button className="btn-close" onClick={resetImportModal}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            
            <div className="modal-body">
              {importProgress ? (
                <div className="import-progress">
                  <div className={`progress-indicator ${importProgress.stage}`}>
                    {importProgress.stage === 'complete' ? (
                      <i className="fas fa-check-circle"></i>
                    ) : importProgress.stage === 'error' ? (
                      <i className="fas fa-exclamation-circle"></i>
                    ) : (
                      <div className="spinner"></div>
                    )}
                  </div>
                  <p className="progress-message">{importProgress.message}</p>
                  {importProgress.stage === 'processing' && (
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${importProgress.percent}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ) : previewLoading ? (
                <div className="import-progress">
                  <div className="progress-indicator">
                    <div className="spinner"></div>
                  </div>
                  <p className="progress-message">Analyzing file...</p>
                </div>
              ) : previewData ? (
                <>
                  <div className="preview-header">
                    <div className="preview-info">
                      <span className="file-name">
                        <i className="fas fa-file"></i>
                        {previewData.filename}
                      </span>
                      <span className="conversation-count">
                        {previewData.total_conversations} conversations found
                      </span>
                    </div>
                    <div className="selection-controls">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={toggleSelectAll}
                      >
                        {selectedConversationsToImport.size === previewData.conversations.length 
                          ? 'Deselect All' 
                          : 'Select All'}
                      </button>
                      <span className="selection-count">
                        {selectedConversationsToImport.size} selected
                      </span>
                    </div>
                  </div>
                  
                  <div className="preview-list">
                    {previewData.conversations.map((conv) => (
                      <div 
                        key={conv.index}
                        className={`preview-item ${selectedConversationsToImport.has(conv.index) ? 'selected' : ''}`}
                        onClick={() => toggleConversationSelection(conv.index)}
                      >
                        <div className="preview-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedConversationsToImport.has(conv.index)}
                            onChange={() => toggleConversationSelection(conv.index)}
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                        <div className="preview-content">
                          <div className="preview-title">{conv.title}</div>
                          <div className="preview-meta">
                            <span><i className="fas fa-message"></i> {conv.message_count} messages</span>
                            {conv.created_at && (
                              <span><i className="fas fa-calendar"></i> {formatDate(conv.created_at)}</span>
                            )}
                          </div>
                          {conv.preview && (
                            <div className="preview-text">{conv.preview}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="preview-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={resetImportModal}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleConfirmImport}
                      disabled={selectedConversationsToImport.size === 0}
                    >
                      <i className="fas fa-download"></i>
                      Import {selectedConversationsToImport.size} Conversation{selectedConversationsToImport.size !== 1 ? 's' : ''}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="import-instructions">
                    <p>{selectedProvider.helpText}</p>
                    <div className="format-info">
                      <i className="fas fa-info-circle"></i>
                      <span>Accepted formats: {selectedProvider.acceptedFormats}</span>
                    </div>
                  </div>
                  
                  <div className="file-upload-area">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={selectedProvider.acceptedFormats}
                      onChange={handleFileSelect}
                      className="file-input"
                      id="context-file-input"
                    />
                    <label htmlFor="context-file-input" className="file-upload-label">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Click to select file or drag & drop</span>
                      <small>{selectedProvider.acceptedFormats}</small>
                    </label>
                  </div>
                  
                  <div className="privacy-reminder">
                    <i className="fas fa-lock"></i>
                    <p>Your data will be encrypted and stored securely. It will only be accessible to you.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon warning">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Confirm Deletion</h3>
            <p>
              {deleteConfirm === 'all' 
                ? 'Are you sure you want to delete ALL your imported personal contexts? This action cannot be undone.'
                : 'Are you sure you want to delete this imported context? This action cannot be undone.'
              }
            </p>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => deleteConfirm === 'all' 
                  ? handleDeleteAllContexts() 
                  : handleDeleteContext(deleteConfirm)
                }
              >
                <i className="fas fa-trash-alt"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Browser Modal */}
      {showConversationBrowser && (
        <div className="modal-overlay" onClick={() => !selectedConversation && setShowConversationBrowser(false)}>
          <div className="conversation-browser-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <i className="fas fa-folder-open"></i>
                <h3>My Conversations</h3>
              </div>
              <button className="btn-close" onClick={() => {
                setShowConversationBrowser(false);
                closeConversationDetail();
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="conversation-browser-content">
              {/* Left Panel - Conversation List */}
              <div className={`conversation-list-panel ${selectedConversation ? 'collapsed' : ''}`}>
                {/* Filters */}
                <div className="conversation-filters">
                  <select 
                    value={selectedImportFilter} 
                    onChange={(e) => handleFilterChange(e.target.value)}
                    className="import-filter-select"
                  >
                    <option value="">All Imports</option>
                    {importedContexts.map(ctx => (
                      <option key={ctx.id} value={ctx.id}>
                        {ctx.original_filename || `${ctx.provider} import`} ({ctx.conversation_count} conv.)
                      </option>
                    ))}
                  </select>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button onClick={handleSearch}>
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>

                {/* Conversation List */}
                <div className="conversation-list">
                  {conversationsLoading ? (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading conversations...</p>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-inbox"></i>
                      <p>No conversations found</p>
                    </div>
                  ) : (
                    <>
                      {conversations.map(conv => (
                        <div 
                          key={conv.id}
                          className={`conversation-item ${selectedConversation === conv.id ? 'selected' : ''}`}
                          onClick={() => loadConversationDetail(conv.id)}
                        >
                          <div className="conv-title">{conv.title}</div>
                          <div className="conv-meta">
                            <span><i className="fas fa-message"></i> {conv.message_count} messages</span>
                            {conv.created_at && (
                              <span><i className="fas fa-calendar"></i> {formatDate(conv.created_at)}</span>
                            )}
                          </div>
                          {conv.preview && conv.preview.length > 0 && (
                            <div className="conv-preview">
                              {conv.preview[0]?.content?.substring(0, 100)}...
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {conversationsPagination.hasMore && (
                        <button className="load-more-btn" onClick={loadMoreConversations}>
                          Load More ({conversationsPagination.total - conversationsPagination.skip - conversations.length} remaining)
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className="conversation-count-info">
                  Showing {conversations.length} of {conversationsPagination.total} conversations
                </div>
              </div>

              {/* Right Panel - Conversation Detail */}
              {selectedConversation && (
                <div className="conversation-detail-panel">
                  <div className="detail-header">
                    <button className="back-btn" onClick={closeConversationDetail}>
                      <i className="fas fa-arrow-left"></i>
                      Back to list
                    </button>
                    {conversationDetail && (
                      <h4>{conversationDetail.title}</h4>
                    )}
                  </div>

                  <div className="messages-container">
                    {loadingDetail ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading messages...</p>
                      </div>
                    ) : conversationDetail ? (
                      <div className="messages-list">
                        {conversationDetail.messages.map((msg, idx) => (
                          <div key={idx} className={`message-bubble ${msg.role}`}>
                            <div className="message-role">
                              {msg.role === 'user' ? (
                                <><i className="fas fa-user"></i> You</>
                              ) : (
                                <><i className="fas fa-robot"></i> Assistant</>
                              )}
                            </div>
                            <div className="message-content">{msg.content}</div>
                            {msg.timestamp && (
                              <div className="message-time">{formatDate(msg.timestamp)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>Failed to load conversation</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
